import { applySnapshot, flow, Instance, types } from 'mobx-state-tree';
import axios from 'axios';
import moment from 'moment';
import ObjectPath from 'object-path';

import { isCardNumberValid, isCVVNumberValid } from '@utils/validators';
import endpoints from '@configs/endpoints';
import { baseRedPayUrl } from '@configs/variables';
import Common from '@store/common';
import getCardInfo from '@utils/getCardInfo';
import { IKushkiPaymentData, IRedPayPaymentData } from '@containers/checkout/payment-form/types';
import { Currencies } from '@store/checkout';
import { Partner } from '@constants/paymentLink';
import sendPaymentError from '@utils/sendPaymentError';
import { TokenResponse } from '@kushki/js/lib/types/remote/token_response';
import { sleep } from '@utils/sleep';
import { getCardType, redPayCardType } from '@utils/cardHelper';
import safeJsonStringify from 'safe-json-stringify';

interface IError {
  code: string;
}

const Card = types.compose(
  Common,
  types.model({
    name: types.optional(types.string, ''),
    number: types.optional(types.string, ''),
    expiryMonth: types.optional(types.string, ''),
    expiryYear: types.optional(types.string, ''),
    cvv: types.optional(types.string, ''),
    zipCode: types.optional(types.string, ''),
    token: types.optional(types.string, ''),
    code: types.optional(types.string, ''),
  }),
);

const CardErrors = types.model({
  name: types.optional(types.boolean, false),
  number: types.optional(types.boolean, false),
  expiryMonth: types.optional(types.boolean, false),
  expiryYear: types.optional(types.boolean, false),
  cvv: types.optional(types.boolean, false),
  zipCode: types.optional(types.boolean, false),
  message: types.optional(types.string, ''),
});

const RedPayStore = types
  .compose(
    Common,
    types.model({
      card: types.optional(Card, {}),
      flags: types.optional(
        types.model({
          requestingAuthToken: types.optional(types.boolean, false),
          requestingCardToken: types.optional(types.boolean, false),
          requestingProcessCardPayment: types.optional(types.boolean, false),
        }),
        {},
      ),
      errors: types.optional(
        types.model({
          card: types.optional(CardErrors, {}),
          process: types.optional(
            types.model({
              cardPayment: types.optional(types.boolean, false),
            }),
            {},
          ),
        }),
        {},
      ),
      success: types.optional(
        types.model({
          cardPayment: types.optional(types.boolean, false),
        }),
        {},
      ),
    }),
  )
  .views((self) => ({
    get cardNumber() {
      return self.card.number.replace(/\s/g, '');
    },
    get expirationDate() {
      return `${self.card.expiryMonth}/${self.card.expiryYear}`;
    },
    get hasCardErrors() {
      return Object.values(self.errors.card).some((value) => value === true);
    },
    get hasCardProcessErrors() {
      return self.errors.process.cardPayment;
    },
    get isProcessing() {
      return (
        self.flags.requestingAuthToken ||
        self.flags.requestingCardToken ||
        self.flags.requestingProcessCardPayment
      );
    },
  }))
  .actions((self) => ({
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    updateField: (field: string, value: any) => {
      ObjectPath.set(self, field, value);
      applySnapshot(self, self);
    },
    updateExpirationDate: (date: string) => {
      const [month, year] = date.split('/');
      self.card.expiryMonth = month;
      self.card.expiryYear = year;
    },
    resetCardData: () => {
      self.card.name = '';
      self.card.number = '';
      self.card.expiryMonth = '';
      self.card.expiryYear = '';
      self.card.cvv = '';
      self.card.zipCode = '';
      self.card.code = '';
    },
    resetCardValidations: () => {
      self.errors.card.name = false;
      self.errors.card.number = false;
      self.errors.card.expiryMonth = false;
      self.errors.card.expiryYear = false;
      self.errors.card.cvv = false;
      self.errors.card.zipCode = false;
      self.errors.card.message = '';
    },
    resetProcessErrors: () => {
      self.errors.process.cardPayment = false;
    },
    resetFlags: () => {
      self.flags.requestingAuthToken = false;
      self.flags.requestingCardToken = false;
      self.flags.requestingProcessCardPayment = false;
    },
    resetSuccessStatus: () => {
      self.success.cardPayment = false;
    },
  }))
  .actions((self) => ({
    resetState: () => {
      self.resetCardData();
      self.resetCardValidations();
      self.resetFlags();
      self.resetSuccessStatus();
    },
    resetTransactionState: () => {
      self.resetFlags();
      self.resetCardValidations();
      self.resetProcessErrors();
      self.resetSuccessStatus();
    },
  }))
  .actions((self) => ({
    validateCardData: () => {
      const now = moment();
      const currentMonth = +now.format('MM');
      const currentYear = +now.format('YY');
      const month = +self.card.expiryMonth;
      const year = +self.card.expiryYear;
      const stringMonth = self.card.expiryMonth;
      const stringYear = self.card.expiryYear;
      const expiryDate = moment(`${stringMonth}/${stringYear}`, 'MM/YY');
      const referenceDate = moment(`${now.format('MM')}/${now.format('YY')}`, 'MM/YY');
      const isValidMonth = month >= 1 && month <= 12 && month >= currentMonth;
      self.errors.card.name = !(self.card.name.length >= 1);
      self.errors.card.number = !isCardNumberValid(self.card.number);
      self.errors.card.cvv = !isCVVNumberValid(self.card.cvv);
      self.errors.card.expiryMonth =
        self.card.expiryYear.length === 2
          ? !expiryDate.isSameOrAfter(referenceDate)
          : !isValidMonth;
      self.errors.card.expiryYear = !(year >= currentYear);
      /*self.errors.card.zipCode =
        self.isZipCodeRequired &&
        !isZipCodeValid(self.card.zipCode, self.locale);*/
      return !self.hasCardErrors;
    },
  }))
  .actions((self) => ({
    getAuthToken: flow(function* (
      merchantId: string,
      publicApiKey: string,
      data: IRedPayPaymentData,
    ) {
      try {
        self.flags.requestingAuthToken = true;

        const { data } = yield axios.post(`${baseRedPayUrl}${endpoints.getAuthToken}`, {
          UserId: merchantId,
          Password: publicApiKey,
        });

        if (data.message === 'Success') {
          self.flags.requestingAuthToken = false;
          return { success: true, authToken: `${data.token}` };
        } else {
          self.flags.requestingAuthToken = false;
          self.flags.requestingProcessCardPayment = false;
          self.errors.process.cardPayment = true;
          self.updateField('errors.card.message', `Algo salio mal intenta nuevamente`);
        }
      } catch (e) {
        self.flags.requestingAuthToken = false;
        self.flags.requestingProcessCardPayment = false;
        self.errors.process.cardPayment = true;
        self.updateField('errors.card.message', `Algo salio mal intenta nuevamente`);

        sendPaymentError({
          ...data,
          partner: Partner.REDPAY,
          paymentMethod: self.globalStore.checkout.paymentMethod,
          error: JSON.stringify(e),
        });
      }

      return { success: false, authToken: '' };
    }),
    getCardToken: flow(function* (paymentData: IRedPayPaymentData, authToken: string) {
      try {
        self.flags.requestingCardToken = true;

        const { data } = yield axios.post(
          `${baseRedPayUrl}${endpoints.getCardToken}`,
          {
            FirstName: paymentData.payeeFirstName,
            LastName: paymentData.payeeLastName,
            Email: paymentData.payeeEmail,
            PhoneNumber: paymentData.payeePhone,
            Street: paymentData.payerAddress,
            City: paymentData.payerCity,
            State: paymentData.payerState,
            Country: paymentData.payerCountry,
            PostalCode: paymentData.payerZipCode,
            CardNumber: paymentData.cardNumber,
            CardExpirationMonth: paymentData.cardExpiration.split('/')[0],
            CardExpirationYear: `20${paymentData.cardExpiration.split('/')[1]}`,
            Cvv: paymentData.cardCvv,
            CardType:
              redPayCardType[getCardType(paymentData.cardNumber) as keyof typeof redPayCardType],
          },
          {
            headers: {
              Authorization: authToken,
            },
          },
        );

        if (data.responseCode === '002') {
          self.flags.requestingCardToken = false;
          return { success: true, cardToken: data.customerCardIdentifier.id };
        } else {
          self.flags.requestingCardToken = false;
          self.flags.requestingProcessCardPayment = false;
          self.errors.process.cardPayment = true;
          self.updateField('errors.card.message', `Algo salio mal intenta nuevamente`);
        }

        return { success: false, cardToken: '' };
      } catch (e) {
        self.flags.requestingCardToken = false;
        self.flags.requestingProcessCardPayment = false;
        self.errors.process.cardPayment = true;
        self.updateField('errors.card.message', `Algo salio mal intenta nuevamente`);

        sendPaymentError({
          ...paymentData,
          partner: Partner.REDPAY,
          paymentMethod: self.globalStore.checkout.paymentMethod,
          error: JSON.stringify(e),
        });
      }
    }),
    initCardTransaction: flow(function* (token: string, payload: IRedPayPaymentData) {
      try {
        self.flags.requestingProcessCardPayment = true;
        const { bin, last4 } = getCardInfo(payload.cardNumber);

        const addressDetails = {
          address: payload.payerAddress,
          city: payload.payerCity,
          region: payload.payerState,
          country: payload.payerCountry,
          zipCode: payload.payerZipCode,
        };

        const body = {
          token,
          paymentLinkId: payload.paymentLinkId,
          payeeFirstName: payload.payeeFirstName,
          payeeLastName: payload.payeeLastName,
          payeeEmail: payload.payeeEmail.trim(),
          payeePhone: payload.payeePhone.replace(/\s/g, ''),
          payeeZipCode: payload.zipCode,
          partner: Partner.REDPAY,
          addressDetails,
          cardInfo: {
            holderName: payload.cardHolderName,
            bin,
            last4,
          },
        };

        const { links } = self.globalStore;
        const { paymentLink } = links;
        const { isSubscription } = paymentLink;

        const { data } = yield axios.post(
          !isSubscription ? endpoints.processCardPayment : endpoints.processCardSubscription,
          body,
        );
        self.flags.requestingProcessCardPayment = false;
        if (data.error) {
          self.errors.process.cardPayment = true;
          self.updateField(
            'errors.card.message',
            `${data.errors.message} - ${data.errors.details}`,
          );
          throw new Error(data.msg);
        }
        self.resetCardData();
        self.success.cardPayment = true;
        self.globalStore.checkout.setCardPaymentSuccess();
      } catch (e: any) {
        self.flags.requestingProcessCardPayment = false;
        self.errors.process.cardPayment = true;
        self.updateField('errors.card.message', `${e.errors.message} - ${e.errors.details}`);
      }
    }),
  }))
  .actions((self) => ({
    processCardPayment: async (
      data: IRedPayPaymentData,
      merchantId: string,
      publicApiKey: string,
    ) => {
      if (!self.flags.requestingAuthToken && !self.flags.requestingProcessCardPayment) {
        self.resetTransactionState();
        try {
          self.flags.requestingProcessCardPayment = true;

          const { success: authTokenSuccess, authToken } = await self.getAuthToken(
            merchantId,
            publicApiKey,
            data,
          );
          if (authTokenSuccess) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const { success: cardTokenSuccess, cardToken } = await self.getCardToken(
              data,
              authToken,
            );
            if (cardTokenSuccess) {
              await self.initCardTransaction(cardToken, data);
            }
          }
        } catch (e) {
          self.flags.requestingAuthToken = false;
          self.flags.requestingProcessCardPayment = false;
          self.errors.process.cardPayment = true;
        }
      }
    },
  }));

export type IRedPayStore = Instance<typeof RedPayStore>;
export default RedPayStore;
